import { Injectable } from '@angular/core';
import {
  CATALOG_PLAN_ACCENT_TOKENS,
  CatalogPlan,
  CatalogPlanAccentToken,
  CatalogPlanEntitlement,
  CatalogPlanEntitlementKind,
} from '@shared/models/plan-catalog.model';
import {
  OrganizationSubscription, PaymentMethod, PlanChangeDirection, PlanChangePreview,
  StartPaymentMethodUpdateResult,
} from '../../domain/models/organization-subscription.model';
import {
  CatalogPlanDto, CatalogPlanEntitlementDto, ChangePlanRequestDto, ClinicSubscriptionSummaryDto,
  PaymentMethodDto, PlanChangePreviewDto, StartPaymentMethodUpdateResultDto,
} from '../dtos/organization-subscription.dto';

const DEFAULT_ACCENT_TOKEN: CatalogPlanAccentToken = 'indigo';
const PLAN_CHANGE_DIRECTIONS: readonly PlanChangeDirection[] = ['upgrade', 'downgrade', 'same'];
const DEFAULT_PLAN_CHANGE_DIRECTION: PlanChangeDirection = 'same';

@Injectable()
export class OrganizationSubscriptionMapper {
  public toOrganizationSubscription(dto: ClinicSubscriptionSummaryDto): OrganizationSubscription {
    return {
      planName: dto.PlanName,
      seatLimit: dto.SeatLimit,
      seatsInUse: dto.SeatsInUse,
      activeSeats: dto.ActiveSeats ?? dto.SeatsInUse,
      pendingSeats: dto.PendingSeats ?? 0,
      hasSubscription: dto.HasSubscription,
      billingPeriod: dto.BillingPeriod,
      status: dto.Status,
      autoChargeEnabled: dto.AutoChargeEnabled,
      commercialPlanId: dto.CommercialPlanId ?? null,
      unitPrice: dto.UnitPrice ?? null,
      currency: dto.Currency ?? null,
      currentPeriodStartUtc: dto.CurrentPeriodStartUtc ?? null,
      currentPeriodEndUtc: dto.CurrentPeriodEndUtc ?? null,
      canManagePaymentMethod: dto.CanManagePaymentMethod ?? false,
      isDemo: dto.IsDemo ?? false,
      trialEndsAtUtc: dto.TrialEndsAtUtc ?? null,
    };
  }

  public toCatalogPlans(dtos: readonly CatalogPlanDto[]): readonly CatalogPlan[] {
    return (dtos ?? []).map((dto) => this.toCatalogPlan(dto));
  }

  public toCatalogPlan(dto: CatalogPlanDto): CatalogPlan {
    return {
      id: dto.Id,
      code: dto.Code,
      name: dto.Name,
      shortDescription: dto.ShortDescription ?? null,
      accentToken: this.toAccentToken(dto.AccentToken),
      monthlyPrice: dto.MonthlyPrice ?? null,
      annualPrice: dto.AnnualPrice ?? null,
      setupFee: dto.SetupFee ?? null,
      trialDays: dto.TrialDays ?? null,
      currency: dto.Currency,
      isMostContracted: dto.IsMostContracted,
      displayOrder: dto.DisplayOrder,
      entitlements: (dto.Entitlements ?? []).map((entitlement) => this.toEntitlement(entitlement)),
    };
  }

  public toPlanChangePreview(dto: PlanChangePreviewDto): PlanChangePreview {
    return {
      targetPlanId: dto.TargetPlanId,
      targetPlanName: dto.TargetPlanName,
      billingPeriod: dto.BillingPeriod,
      direction: this.toDirection(dto.Direction),
      currentPlanId: dto.CurrentPlanId,
      currentPlanName: dto.CurrentPlanName,
      currentUnitPrice: dto.CurrentUnitPrice,
      targetUnitPrice: dto.TargetUnitPrice,
      currency: dto.Currency,
      seatsInUse: dto.SeatsInUse,
      targetSeatLimit: dto.TargetSeatLimit,
      exceedsSeatLimit: dto.ExceedsSeatLimit,
      seatsOverLimit: dto.SeatsOverLimit,
      modulesLost: dto.ModulesLost ?? [],
      modulesGained: dto.ModulesGained ?? [],
      amountDueNow: dto.AmountDueNow,
      isProrated: dto.IsProrated,
      isGatewayManaged: dto.IsGatewayManaged,
      violations: (dto.Violations ?? []).map((violation) => ({
        key: violation.Key,
        used: violation.Used,
        pending: violation.Pending,
        projected: violation.Projected,
        limit: violation.Limit,
        requestedDelta: violation.RequestedDelta,
        overBy: violation.OverBy,
      })),
    };
  }

  public toChangePlanRequest(planId: string, billingPeriod: string): ChangePlanRequestDto {
    return { PlanId: planId, BillingPeriod: billingPeriod };
  }

  public toPaymentMethod(dto: PaymentMethodDto): PaymentMethod {
    return {
      type: dto.Type ?? 'card',
      brand: dto.Brand,
      last4: dto.Last4,
      expMonth: dto.ExpMonth,
      expYear: dto.ExpYear,
    };
  }

  public toStartPaymentMethodUpdateResult(
    dto: StartPaymentMethodUpdateResultDto,
  ): StartPaymentMethodUpdateResult {
    return {
      clientSecret: dto.ClientSecret,
      publishableKey: dto.PublishableKey,
      setupIntentId: dto.SetupIntentId,
    };
  }

  private toEntitlement(dto: CatalogPlanEntitlementDto): CatalogPlanEntitlement {
    return {
      id: dto.Id,
      kind: this.toEntitlementKind(dto.Kind),
      entitlementKey: dto.EntitlementKey,
      limitValue: dto.LimitValue ?? null,
      isUnlimited: dto.IsUnlimited,
      isIncluded: dto.IsIncluded,
      displayValue: dto.DisplayValue ?? null,
      monthlySurcharge: dto.MonthlySurcharge ?? null,
      displayOrder: dto.DisplayOrder,
    };
  }

  private toAccentToken(value: string): CatalogPlanAccentToken {
    const normalized = (value ?? '').trim().toLowerCase();
    return CATALOG_PLAN_ACCENT_TOKENS.find((token) => token === normalized) ?? DEFAULT_ACCENT_TOKEN;
  }

  private toEntitlementKind(value: string): CatalogPlanEntitlementKind {
    return (value ?? '').trim().toLowerCase() === 'module' ? 'module' : 'limit';
  }

  private toDirection(value: string): PlanChangeDirection {
    const normalized = (value ?? '').trim().toLowerCase();
    return PLAN_CHANGE_DIRECTIONS.find((direction) => direction === normalized)
      ?? DEFAULT_PLAN_CHANGE_DIRECTION;
  }
}
